import React, { useState, useRef, useEffect } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  changePassAction,
  loadingToggleAction,
  passChangeAction
} from '../../store/actions/AuthActions';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { colors } from '../config/colors';

const ChangePasswordModal = ({
  isOpen,
  toggleModal,
  onClose,
  showLoading,
  passChanged
}) => {
  let errorsObj = {
    oldPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  };
  const [errors, setErrors] = useState(errorsObj);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const firstInputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (passChanged == true) {
      onClose();
      dispatch(passChangeAction(false));
    }
  }, [passChanged]);

  useEffect(() => {
    if (isOpen && firstInputRef.current) {
      firstInputRef.current.focus();
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  function onChangePassword(e) {
    e.preventDefault();
    let error = false;
    const errorObj = { ...errorsObj };

    if (oldPassword == '') {
      errorObj.oldPassword = 'Old Password is Required';
      error = true;
    }

    if (newPassword == '') {
      errorObj.newPassword = 'New Password is Required';
      error = true;
    } else {
      const passwordErrors = validatePassword(newPassword);
      if (passwordErrors.length > 0) {
        errorObj.newPassword = passwordErrors.join(', ');
        error = true;
      }
    }

    if (newPassword == oldPassword) {
      errorObj.newPassword = 'New Password cannot be same as Old Password';
      error = true;
    }

    if (confirmNewPassword == '') {
      errorObj.confirmNewPassword = 'Confirm New Password is Required';
      error = true;
    } else if (confirmNewPassword != newPassword) {
      errorObj.confirmNewPassword = 'New Password and Confirm New Password do not match';
      error = true;
    }

    setErrors(errorObj);
    if (error) {
      return;
    }
    dispatch(loadingToggleAction(true));
    dispatch(changePassAction(oldPassword, newPassword));
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={toggleModal}
        >
          <div
            className="bg-white p-8 rounded-2xl shadow-xl mx-auto w-full max-w-md relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              onClick={onClose}
            >
              <svg
                aria-hidden="true"
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
            
            <div className="bg-white rounded-2xl">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-light/5 to-transparent -mx-8 -mt-8 mb-6 rounded-t-2xl">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-headings" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-primary-headings">Change Password</h2>
                </div>
              </div>
              
              <form className="space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-accent-dark">
                    Old Password <span className="text-cta">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showOldPassword ? 'text' : 'password'}
                      className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                      value={oldPassword}
                      placeholder="Type Your Password"
                      onChange={(e) => setOldPassword(e.target.value)}
                      ref={firstInputRef}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark hover:text-primary-headings transition-colors duration-200"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                    >
                      {showOldPassword ? (
                        <FaEye size={18} />
                      ) : (
                        <FaEyeSlash size={18} />
                      )}
                    </button>
                  </div>
                  {errors.oldPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.oldPassword}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-accent-dark">
                    New Password <span className="text-cta">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                      value={newPassword}
                      placeholder="Password"
                      onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark hover:text-primary-headings transition-colors duration-200"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <FaEye size={18} />
                      ) : (
                        <FaEyeSlash size={18} />
                      )}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.newPassword}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-accent-dark">
                    Confirm New Password <span className="text-cta">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmNewPassword ? 'text' : 'password'}
                      className="w-full px-4 py-2.5 bg-accent-light/5 border border-accent-light/30 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-light focus:border-transparent"
                      value={confirmNewPassword}
                      placeholder="Confirm Password"
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-dark hover:text-primary-headings transition-colors duration-200"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    >
                      {showConfirmNewPassword ? (
                        <FaEye size={18} />
                      ) : (
                        <FaEyeSlash size={18} />
                      )}
                    </button>
                  </div>
                  {errors.confirmNewPassword && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.confirmNewPassword}
                    </p>
                  )}
                </div>

                <div className="mt-8 pt-6 border-t border-accent-light/20">
                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      className="px-6 py-2.5 border border-accent-light/30 rounded-lg text-accent-dark hover:bg-accent-light/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-light transition-colors duration-200"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2.5 bg-cta text-white rounded-lg hover:bg-cta/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cta transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      disabled={showLoading}
                      onClick={onChangePassword}
                    >
                      {showLoading ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      ) : (
                        'Change Password'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    errorMessage: state.auth.errorMessage,
    successMessage: state.auth.successMessage,
    showLoading: state.auth.showLoading,
    passChanged: state.auth.passChanged
  };
};
export default connect(mapStateToProps)(ChangePasswordModal);
